import { useCallback, useSyncExternalStore } from 'react';

import type { ShippingQuote } from '@/features/storefront/api/storefront';
import type { StorefrontDataSource } from '@/features/storefront/data/source';
import { PIN_CODE } from '@/features/storefront/delivery';

/**
 * The shopper's delivery PIN code, shared by every box that asks for it.
 *
 * The product page's delivery check and the header's "Deliver to" control read
 * and write the same remembered PIN code, so a shopper who types it in one sees
 * it in the other. Kept per shop source, like the cart token, in localStorage;
 * a change is announced on `window` so every reader in this tab follows at
 * once, and the `storage` event carries it to other tabs.
 */

const PINCODE_KEY = 'neon_delivery_pincode';
const CHANGED = 'neon:delivery-pincode';

/** The storage key for one shop source: the live shop's plain, any other suffixed. */
export function pincodeKey(sourceKey: string): string {
  return sourceKey === 'live' ? PINCODE_KEY : `${PINCODE_KEY}:${sourceKey}`;
}

export function readPincode(sourceKey: string): string {
  try {
    const saved = localStorage.getItem(pincodeKey(sourceKey)) ?? '';
    return PIN_CODE.test(saved) ? saved : '';
  } catch {
    return ''; // private browsing, storage disabled
  }
}

export function writePincode(sourceKey: string, pincode: string): void {
  try {
    localStorage.setItem(pincodeKey(sourceKey), pincode);
  } catch {
    // A pincode that is not remembered is asked for again; nothing breaks.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(CHANGED));
}

function subscribe(listener: () => void): () => void {
  window.addEventListener(CHANGED, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(CHANGED, listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * The remembered PIN code and a way to change it. `enabled: false` - the
 * customiser - neither reads nor writes: the dashboard is not the shop.
 */
export function useDeliveryPincode(
  sourceKey: string,
  enabled = true,
): [string, (pincode: string) => void] {
  const pincode = useSyncExternalStore(
    subscribe,
    () => (enabled ? readPincode(sourceKey) : ''),
    () => '',
  );
  const save = useCallback(
    (next: string) => {
      if (enabled) writePincode(sourceKey, next);
    },
    [enabled, sourceKey],
  );
  return [pincode, save];
}

/**
 * Whether the shop delivers to a PIN code at all, without a product or a
 * basket: the shop's own delivery quote for one parcel of its default size.
 */
export function checkPincode(source: StorefrontDataSource, pincode: string): Promise<ShippingQuote> {
  return source.shippingOptions({ postalCode: pincode });
}
